import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useGithub } from '../hooks/useGithub';
import { Repository } from '../types';

const RepositoriesPage: React.FC = () => {
  const { auth, loading, repositories, fetchRepositories, selectRepository } = useGithub();
  
  // Fetch repositories on mount if authenticated
  useEffect(() => {
    if (auth && repositories.length === 0) {
      fetchRepositories();
    }
  }, [auth, repositories.length, fetchRepositories]);
  
  // Handle selection and navigation
  const handleSelectRepository = (repo: Repository) => {
    selectRepository(repo);
  };
  
  if (!auth) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-6">My Repositories</h1>
          
          <div className="bg-yellow-50 p-6 rounded-lg shadow-md border border-yellow-200">
            <p className="text-yellow-800 mb-4">
              Please login with GitHub to view your repositories.
            </p>
            <Link to="/" className="github-btn">
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">My Repositories</h1>
          
          <button 
            onClick={() => fetchRepositories()} 
            className="outline-btn flex items-center"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-t-transparent border-primary rounded-full mr-2"></div>
                Refreshing...
              </>
            ) : (
              <>Refresh</>
            )}
          </button>
        </div>
        
        {loading && repositories.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin mx-auto mb-4 w-10 h-10 border-4 border-t-transparent border-primary rounded-full"></div>
            <p className="text-gray-600">Loading repositories...</p>
          </div>
        ) : repositories.length === 0 ? (
          <div className="bg-yellow-50 p-6 rounded-lg shadow-md border border-yellow-200 text-center">
            <p className="text-yellow-800 mb-4">
              No repositories found for your account.
            </p>
            <p className="text-sm text-yellow-700 mb-4">
              Repositories might be private or you might not have any repositories yet.
            </p>
            <a 
              href="https://github.com/new" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="github-btn inline-block"
            >
              Create a Repository
            </a>
          </div>
        ) : (
          <div className="grid gap-4">
            {repositories.map((repo) => (
              <div 
                key={repo.id} 
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-primary mb-1">{repo.name}</h2>
                    <p className="text-gray-600 mb-4">{repo.description || 'No description provided'}</p>
                  </div>
                  <div className="flex space-x-2">
                    <a 
                      href={repo.html_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </a>
                  </div>
                </div>
                
                <div className="mt-4 flex space-x-3">
                  <Link 
                    to="/editor" 
                    className="github-btn py-1 px-3 text-sm"
                    onClick={() => handleSelectRepository(repo)}
                  >
                    Edit with HTML Tool
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepositoriesPage; 